import { Code } from '@connectrpc/connect'
import type { SubmitterMaster } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { SubmitterMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/SubmitterMasterFormFields'
import { useGetSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useGetSubmitterMaster'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { useUpdateSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useUpdateSubmitterMaster'
import {
  type SubmitterMasterFormInput,
  SubmitterMasterFormSchema,
  type SubmitterMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/schema'
import { resolveSubmitterMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/serverError'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: SubmitterMasterRow
  onClose: () => void
}

const SUBMITTER_EDIT_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.submitterMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.submitterMaster.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.submitterMaster.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.submitterMaster.serverError.invalidArgument',
}

const toFormValues = (fetched: SubmitterMaster): SubmitterMasterFormInput => ({
  submitterCode: fetched.submitterCode,
  submitterName: fetched.submitterName,
  licenseType: fetched.licenseType,
  areaCode: fetched.areaCode,
  businessMasterId: fetched.businessMasterId,
  note: fetched.note ?? '',
  validFrom:
    fetched.validFrom !== undefined
      ? {
          year: fetched.validFrom.year,
          month: fetched.validFrom.month,
          day: fetched.validFrom.day,
        }
      : todayCivilDate(),
  validTo:
    fetched.validTo !== undefined
      ? {
          year: fetched.validTo.year,
          month: fetched.validTo.month,
          day: fetched.validTo.day,
        }
      : undefined,
  members: fetched.members.map((m) => ({
    bgMasterId: m.bgMasterId,
    bgName: m.bgName,
    validFrom:
      m.validFrom !== undefined
        ? {
            year: m.validFrom.year,
            month: m.validFrom.month,
            day: m.validFrom.day,
          }
        : todayCivilDate(),
    validTo:
      m.validTo !== undefined
        ? {
            year: m.validTo.year,
            month: m.validTo.month,
            day: m.validTo.day,
          }
        : undefined,
  })),
})

export const EditSubmitterMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateSubmitterMaster()
  const getQuery = useGetSubmitterMaster(row.id)
  const { isError, error } = getQuery

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    isDirty,
    loadStatus,
  } = useEditModalForm<
    SubmitterMaster,
    SubmitterMasterFormInput,
    SubmitterMasterFormValues
  >({
    source: entitySourceOf(getQuery, (d) => d.submitterMaster),
    toFormValues,
    schema: SubmitterMasterFormSchema,
    defaultValues: {
      submitterCode: '',
      submitterName: '',
      licenseType: row.licenseType,
      areaCode: row.areaCode,
      businessMasterId: '',
      note: '',
      validFrom: todayCivilDate(),
      validTo: undefined,
      members: [],
    },
    onSubmit: async (values, { expectedUpdatedAt }) => {
      await mutateAsync({
        id: row.id,
        submitterName: values.submitterName,
        businessMasterId: values.businessMasterId,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // valid_from は update request で required (proto field 8, mutable)。
        // 送信し忘れると BFF protovalidate が InvalidArgument を返し編集不可
        // になる (issue #5913)。
        validFrom: values.validFrom,
        validTo: values.validTo,
        members: values.members.map((m) => ({
          bgMasterId: m.bgMasterId,
          bgName: m.bgName,
          validFrom: m.validFrom,
          validTo: m.validTo,
        })),
        expectedUpdatedAt,
      })
    },
    onClose,
    customErrorKeys: SUBMITTER_EDIT_ERROR_KEYS,
    unknownErrorKey:
      'page:adminOcctoMaster.submitterMaster.serverError.unknown',
    resolveDetailedError: resolveSubmitterMasterDetailedError,
  })

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      loadError={
        isError ? (
          <Alert
            status="error"
            title={mapMasterGetError(
              t,
              error,
              'page:adminOcctoMaster.submitterMaster.serverError',
            )}
          />
        ) : undefined
      }
      loadStatus={loadStatus}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #sm-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t('page:adminOcctoMaster.submitterMaster.modal.editTitle')}
    >
      <SubmitterMasterFormFields
        control={control}
        errors={errors}
        isEdit
        setValue={setValue}
      />
    </CrudModalLayout>
  )
}
